// src/components/TaskDetailsSidebar.tsx
import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiX, FiCalendar, FiFlag, FiTag, FiCheckSquare, FiPlus } from "react-icons/fi";
import type { Task, Label, Priority, TaskStatus, Subtask } from "../../types/domain";
import { updateTask, deleteTask } from "../../services/tasks";
import { listSubtasks, createSubtask, updateSubtask, deleteSubtask } from "../../services/subtasks";
import { replaceTaskLabels } from "../../services/labels";
import { confirm } from "../../lib/dialogs";
import { toast } from "react-hot-toast";
import "../TaskDetailsSidebar.css";

interface TaskDetailsSidebarProps {
  task: Task | null;
  labels: Label[];
  allLabels: Label[];
  isOpen: boolean;
  onClose: () => void;
  onTaskUpdated: () => void;
  onTaskDeleted?: () => void;
}

const STATUS_OPTIONS: { value: TaskStatus; label: string }[] = [
  { value: "backlog", label: "Backlog" },
  { value: "todo", label: "To Do" },
  { value: "in_progress", label: "En Progreso" },
  { value: "in_review", label: "En Revisión" },
  { value: "done", label: "Completado" },
  { value: "archived", label: "Archivado" },
];

const PRIORITY_OPTIONS: { value: Priority; label: string; color: string }[] = [
  { value: "low", label: "Baja", color: "#4CAF50" },
  { value: "medium", label: "Media", color: "#FF9800" },
  { value: "high", label: "Alta", color: "#F44336" },
  { value: "urgent", label: "Urgente", color: "#9C27B0" },
];

export default function TaskDetailsSidebar({
  task,
  labels,
  allLabels,
  isOpen,
  onClose,
  onTaskUpdated,
  onTaskDeleted
}: TaskDetailsSidebarProps) {
  // Form states
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<Priority | "">("");
  const [status, setStatus] = useState<TaskStatus>("todo");
  const [deadline, setDeadline] = useState("");
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>([]);
  
  // Subtasks
  const [subtasks, setSubtasks] = useState<Subtask[]>([]);
  const [newSubtaskTitle, setNewSubtaskTitle] = useState("");
  const [loadingSubtasks, setLoadingSubtasks] = useState(false);
  
  // UI states
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "subtasks">("details");

  const loadSubtasks = useCallback(async () => {
    if (!task) return;
    setLoadingSubtasks(true);
    try {
      const { data } = await listSubtasks(task.id);
      setSubtasks(data || []);
    } catch (error) {
      console.error("Error loading subtasks:", error);
    } finally {
      setLoadingSubtasks(false);
    }
  }, [task]);

  // Update form when task changes
  useEffect(() => {
    if (task) {
      setTitle(task.title || "");
      setDescription(task.description || "");
      setPriority(task.priority || "");
      setStatus(task.status);
      setDeadline(task.deadline ? toInputDate(task.deadline) : "");
      setSelectedLabelIds(labels.map(l => l.id));
      loadSubtasks();
    }
  }, [task, labels, loadSubtasks]);

  const handleSave = async () => {
    if (!task) return;
    
    setSaving(true);
    try {
      // Update task
      const taskUpdate = {
        title: title.trim() || task.title,
        description: description.trim() || null,
        priority: priority || null,
        status,
        deadline: deadline || null,
      };
      
      const { error: taskError } = await updateTask(task.id, taskUpdate);
      if (taskError) throw new Error("Error updating task");
      
      // Update labels
      const { error: labelError } = await replaceTaskLabels(task.id, selectedLabelIds);
      if (labelError) throw new Error("Error updating labels");
      
      toast.success("Tarea actualizada");
      onTaskUpdated();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Error al guardar los cambios");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    
    const confirmed = await confirm({
      title: "Eliminar tarea",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      confirmText: "Eliminar",
    });
    
    if (!confirmed) return;
    
    try {
      await deleteTask(task.id);
      toast.success("Tarea eliminada");
      onTaskDeleted?.();
      onClose();
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Error al eliminar la tarea");
    }
  };

  const handleAddSubtask = async () => {
    if (!task || !newSubtaskTitle.trim()) return;
    
    try {
      await createSubtask({ task_id: task.id, title: newSubtaskTitle.trim() });
      setNewSubtaskTitle("");
      toast.success("Subtarea creada");
      loadSubtasks();
    } catch (error) {
      console.error("Error creating subtask:", error);
      toast.error("Error al crear la subtarea");
    }
  };

  const handleToggleSubtask = async (subtask: Subtask) => {
    try {
      await updateSubtask(subtask.id, { done: !subtask.done });
      setSubtasks(prev => 
        prev.map(s => s.id === subtask.id ? { ...s, done: !s.done } : s)
      );
    } catch (error) {
      console.error("Error toggling subtask:", error);
      toast.error("Error al actualizar la subtarea");
    }
  };

  const handleDeleteSubtask = async (subtask: Subtask) => {
    const confirmed = await confirm({
      title: "Eliminar subtarea",
      text: "Esta acción no se puede deshacer.",
      icon: "warning",
      confirmText: "Eliminar",
    });
    
    if (!confirmed) return;
    
    try {
      await deleteSubtask(subtask.id);
      setSubtasks(prev => prev.filter(s => s.id !== subtask.id));
      toast.success("Subtarea eliminada");
    } catch (error) {
      console.error("Error deleting subtask:", error);
      toast.error("Error al eliminar la subtarea");
    }
  };

  const completedSubtasks = subtasks.filter(s => s.done).length;
  const progressPercentage = subtasks.length > 0 ? (completedSubtasks / subtasks.length) * 100 : 0;

  if (!task) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
            className="task-details-sidebar"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
          >
            {/* Header */}
            <div className="sidebar-header">
              <button className="close-btn" onClick={onClose}>
                <FiX size={20} />
              </button>
              <div className="sidebar-title">
                <h2>Detalles de la tarea</h2>
              </div>
              <div></div> {/* Spacer for centering */}
            </div>

            {/* Tabs */}
            <div className="sidebar-tabs">
              <button 
                className={`tab-btn ${activeTab === "details" ? "active" : ""}`}
                onClick={() => setActiveTab("details")}
              >
                Detalles
              </button>
              <button 
                className={`tab-btn ${activeTab === "subtasks" ? "active" : ""}`}
                onClick={() => setActiveTab("subtasks")}
              >
                Subtareas ({subtasks.length})
              </button>
            </div>

            {/* Content */}
            <div className="sidebar-content">
              {activeTab === "details" ? (
                <div className="details-tab">
                  {/* Title */}
                  <div className="field-group">
                    <label className="field-label">Título</label>
                    <input
                      type="text"
                      className="field-input"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Título de la tarea"
                    />
                  </div>

                  {/* Description */}
                  <div className="field-group">
                    <label className="field-label">Descripción</label>
                    <textarea
                      className="field-textarea"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder="Descripción de la tarea"
                      rows={4}
                    />
                  </div>

                  {/* Status & Priority Row */}
                  <div className="field-row">
                    <div className="field-group">
                      <label className="field-label">
                        <FiFlag size={14} />
                        Estado
                      </label>
                      <select
                        className="field-select"
                        value={status}
                        onChange={(e) => setStatus(e.target.value as TaskStatus)}
                      >
                        {STATUS_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="field-group">
                      <label className="field-label">
                        <FiFlag size={14} />
                        Prioridad
                      </label>
                      <select
                        className="field-select"
                        value={priority}
                        onChange={(e) => setPriority(e.target.value as Priority | "")}
                      >
                        <option value="">Sin prioridad</option>
                        {PRIORITY_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Deadline */}
                  <div className="field-group">
                    <label className="field-label">
                      <FiCalendar size={14} />
                      Fecha límite
                    </label>
                    <input
                      type="date"
                      className="field-input"
                      value={deadline}
                      onChange={(e) => setDeadline(e.target.value)}
                    />
                  </div>

                  {/* Labels */}
                  <div className="field-group">
                    <label className="field-label">
                      <FiTag size={14} />
                      Etiquetas
                    </label>
                    <div className="labels-grid">
                      {allLabels.map(label => (
                        <label key={label.id} className="label-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedLabelIds.includes(label.id)}
                            onChange={() => {
                              if (selectedLabelIds.includes(label.id)) {
                                setSelectedLabelIds(prev => prev.filter(id => id !== label.id));
                              } else {
                                setSelectedLabelIds(prev => [...prev, label.id]);
                              }
                            }}
                          />
                          <span 
                            className="label-swatch" 
                            style={{ backgroundColor: label.color }}
                          />
                          <span className="label-name">{label.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="subtasks-tab">
                  {/* Progress */}
                  <div className="subtasks-progress">
                    <div className="progress-header">
                      <span className="progress-text">
                        {completedSubtasks} de {subtasks.length} completadas
                      </span>
                      <span className="progress-percentage">
                        {Math.round(progressPercentage)}%
                      </span>
                    </div>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill" 
                        style={{ width: `${progressPercentage}%` }}
                      />
                    </div>
                  </div>

                  {/* Add new subtask */}
                  <div className="add-subtask">
                    <input
                      type="text"
                      className="field-input"
                      value={newSubtaskTitle}
                      onChange={(e) => setNewSubtaskTitle(e.target.value)}
                      placeholder="Nueva subtarea..."
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddSubtask();
                        }
                      }}
                    />
                    <button 
                      className="add-subtask-btn"
                      onClick={handleAddSubtask}
                      disabled={!newSubtaskTitle.trim()}
                    >
                      <FiPlus size={16} />
                    </button>
                  </div>

                  {/* Subtasks list */}
                  <div className="subtasks-list">
                    {loadingSubtasks ? (
                      <div className="loading-text">Cargando subtareas...</div>
                    ) : subtasks.length === 0 ? (
                      <div className="empty-text">No hay subtareas</div>
                    ) : (
                      subtasks.map(subtask => (
                        <div 
                          key={subtask.id} 
                          className={`subtask-item ${subtask.done ? "completed" : ""}`}
                        >
                          <button
                            className="subtask-checkbox"
                            onClick={() => handleToggleSubtask(subtask)}
                          >
                            <FiCheckSquare size={16} />
                          </button>
                          <span className="subtask-title">
                            {subtask.title}
                          </span>
                          <button
                            className="subtask-delete"
                            onClick={() => handleDeleteSubtask(subtask)}
                          >
                            <FiX size={14} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="sidebar-footer">
              <button 
                className="btn-secondary"
                onClick={handleDelete}
              >
                Eliminar
              </button>
              <button 
                className="btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          </motion.div>
      )}
    </AnimatePresence>
  );
}

// Helper function
function toInputDate(value: string): string {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}